import Image from "next/image";
import { Button } from "@/components/ui/button";
import type { DesignPreviewSyncKind } from "@/lib/designer/previewFreshness";

export type DressDesignView = "front" | "back";

interface DesignPreviewPortraitProps {
  side: DressDesignView;
  imageUrl: string | null;
  syncKind: DesignPreviewSyncKind | null;
  showCaption?: boolean;
  showEmptyStand: boolean;
  generating: boolean;
  readyActionLabel?: string;
  followPanel?: boolean;
  staleAction?: { label: string; onClick: () => void; disabled: boolean };
  showInlineStaleAction?: boolean;
  onOpenFullscreen?: (view: DressDesignView) => void;
  onEditInChat?: () => void;
}

/** A stable portrait surface; the workspace owns rendering and action policy. */
export default function DesignPreviewPortrait({
  side,
  imageUrl,
  syncKind,
  showCaption = false,
  showEmptyStand,
  generating,
  readyActionLabel,
  followPanel = false,
  staleAction,
  showInlineStaleAction = false,
  onOpenFullscreen,
  onEditInChat,
}: DesignPreviewPortraitProps) {
  const sideLabel = side === "front" ? "Front" : "Back";
  const preview =
    imageUrl || showEmptyStand ? (
      <Image
        src={imageUrl ?? "/dress_stand.png"}
        alt={
          imageUrl
            ? `${sideLabel} view of your current dress design`
            : "Empty dress stand ready for your design"
        }
        fill
        sizes="(min-width: 1024px) 40vw, 85vw"
        quality={90}
        className="object-contain object-center"
      />
    ) : null;

  return (
    <div
      className="designer-preview-portrait flex h-full min-h-0 min-w-0 flex-1 flex-col"
      data-slot={`designer-${side}-portrait`}
    >
      {showCaption ? (
        <p className="designer-preview-caption">
          <span className="font-medium text-foreground">{sideLabel}</span>
          {staleAction ? (
            <button
              type="button"
              className="text-xs font-medium text-foreground underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              onClick={staleAction.onClick}
              disabled={staleAction.disabled}
              aria-label={`Back is out of date. ${staleAction.label}`}
            >
              {staleAction.label}
            </button>
          ) : (
            <span className="designer-preview-state">
              {syncKind === "dirty"
                ? "Update needed"
                : imageUrl
                  ? syncKind === "unknown"
                    ? "Saved preview"
                    : "Saved"
                  : ""}
            </span>
          )}
        </p>
      ) : null}
      <div className="designer-preview-image relative min-h-0 w-full flex-1">
        {imageUrl && onOpenFullscreen ? (
          <button
            type="button"
            className="absolute inset-0 cursor-zoom-in rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            aria-label={`Enlarge ${side} dress image`}
            aria-haspopup="dialog"
            onClick={() => onOpenFullscreen(side)}
          >
            {preview}
          </button>
        ) : preview ? (
          preview
        ) : generating ? null : (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
            <p className="text-sm font-medium">
              {readyActionLabel
                ? `Your ${side} design is ready`
                : `No ${side} preview yet`}
            </p>
            <p className="max-w-52 text-xs leading-5 text-muted-foreground">
              {readyActionLabel
                ? followPanel
                  ? "Check the update below for your next step."
                  : readyActionLabel === "Apply"
                    ? "Apply your changes below."
                    : "Generate the preview below."
                : followPanel
                  ? "Check the update below for your next step."
                  : `Describe the ${side} in chat when you’re ready to add it.`}
            </p>
            {onEditInChat && !readyActionLabel && !followPanel ? (
              <Button
                type="button"
                variant="outline"
                className="designer-control mt-2"
                onClick={onEditInChat}
              >
                Continue in chat
              </Button>
            ) : null}
          </div>
        )}
      </div>
      {staleAction && showInlineStaleAction ? (
        <div className="flex shrink-0 items-center justify-between gap-2 px-1 text-xs text-muted-foreground">
          <span>Back is out of date</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 shrink-0"
            onClick={staleAction.onClick}
            disabled={staleAction.disabled}
          >
            {staleAction.label}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
